import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { SERVER_BASE_URL } from '../../../environments/environment';
import { CreditOptionDto } from '../../model/credit/credit-option.dto';
import { PaymentRequestDto } from '@app/model/payment-request/payment-request.dto';

@Injectable({
  providedIn: 'root',
})
export class CreditService {
  private readonly httpReqOptions = { withCredentials: true };

  constructor(private http: HttpClient) {}

  getPaymentRequestPublicInfo(
    paymentRequestId: string
  ): Observable<PaymentRequestDto> {
    return this.http.get<PaymentRequestDto>(
      `${SERVER_BASE_URL}/request/public/${paymentRequestId}`
    );
  }

  fetchInvoiceTerms(paymentMethodId: string): Observable<CreditOptionDto> {
    return this.http.get<CreditOptionDto>(
      `${SERVER_BASE_URL}/request/invoice/terms/${paymentMethodId}`,
      { withCredentials: true }
    );
  }

  fetchInstallments(
    selectedMethodId: string
  ): Observable<InstallmentOptionsDto> {
    return this.http.get<InstallmentOptionsDto>(
      `${SERVER_BASE_URL}/request/installments/plans/${selectedMethodId}`,
      { withCredentials: true }
    );
  }

  instantDebitPrecheck(
    instantDebitPaymentRequestId: string
  ): Observable<InstantDebitPrecheckResponse> {
    return this.http.get<InstantDebitPrecheckResponse>(
      `${SERVER_BASE_URL}/instantdebit/precheck?taskId=${instantDebitPaymentRequestId}`
    );
  }

  initInstantDebit(): Observable<InstantDebitInitResponse> {
    return this.http.get<InstantDebitInitResponse>(
      `${SERVER_BASE_URL}/instantdebit/init`
    );
  }

  getIfFurtherAISIsRequired(selectedMethodId: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${SERVER_BASE_URL}/credit/se/requiresFurtherAIS/${selectedMethodId}`,
      this.httpReqOptions
    );
  }

  postFinsharkUrl(
    redirectUrlParams: string,
    useIframe?: boolean
  ): Promise<FinsharkUrlResponse> {
    return this.http
      .post<FinsharkUrlResponse>(
        `${SERVER_BASE_URL}/credit/se/finshark/url/?useIframe=${useIframe}`,
        redirectUrlParams,
        this.httpReqOptions
      )
      .toPromise();
  }

  postFinsharkResults(result: FinsharkResult, selectedMethodId: string) {
    return this.http
      .post(
        `${SERVER_BASE_URL}/credit/se/finshark/results/${selectedMethodId}`,
        result,
        this.httpReqOptions
      )
      .toPromise();
  }
}

export interface InstantDebitInitResponse {
  instantDebitPaymentRequestId: string;
}

export enum InstantDebitStatus {
  CREATED = 'CREATED',
  PENDING_ACCOUNT_NOTIFICATION = 'PENDING_ACCOUNT_NOTIFICATION',
  PENDING_PRECHECK = 'PENDING_PRECHECK',
  PENDING_PERSON_CREATION = 'PENDING_PERSON_CREATION',
  FAILED = 'FAILED',
  SUCCESS = 'SUCCESS',
}

export enum InstantDebitFailureReason {
  CREDIT_PRECHECK_FAILED = 'CREDIT_PRECHECK_FAILED',
  MANDATE_DENIED = 'MANDATE_DENIED',
}

export interface InstantDebitPrecheckResponse {
  result: string;
  error: InstanDebitErrorMessage;
  instantDebitPaymentRequestId: string;
  status: InstantDebitStatus;
  failureReason: InstantDebitFailureReason;
}

export interface InstanDebitErrorMessage {
  title: string;
  message: string;
}

export class InstallmentOptionsDto {
  preferredInstallmentPlanDurations: number[];
  installmentOptions: CreditOptionDto[];
}

export class SuccessResponse {
  success: boolean;
}

export class LoanApplicationResponse {
  granted: boolean;
}

export interface FinsharkUrlResponse {
  url?: string;
}

export interface FinsharkResult {
  authorizationId: string;
  sessionId: string;
  result: string;
  verifiedAccountId: string;
  ownerName: string;
}

export enum ResultStatus {
  SUCCESS = 'success',
  ERROR = 'error',
  CANCELED = 'canceled',
}
